import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from "react-router-dom";
import { AuthContext } from '../AuthContext';
import Swal from 'sweetalert2';
import './AdminImageManager.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function AdminImageManager() {
  const navigate = useNavigate();
  const { authFetch } = useContext(AuthContext);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedType, setSelectedType] = useState('advertisement');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);

  useEffect(() => {
    const fetchImages = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/Images?type=${selectedType}`);
        if (!response.ok) {
          throw new Error('Failed to fetch images');
        }
        const data = await response.json();
        setImages(data);
        setError(null);
      } catch (err) {
        setError('Failed to load images. Please try again.');
        setImages([]);
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, [selectedType]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {

      if (!file.type.startsWith('image/')) {
        Swal.fire({
          title: 'Error!',
          text: 'Please select only image files (JPG, PNG, GIF, etc.)',
          icon: 'error',
          confirmButtonColor: '#45a049'
        });

        e.target.value = '';
        setSelectedFile(null);
        setPreviewUrl('');
        return;
      }

      setSelectedFile(file);
      const fileReader = new FileReader();
      fileReader.onloadend = () => {
        setPreviewUrl(fileReader.result);
      };
      fileReader.readAsDataURL(file);
    }
  };

  const handleImageUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      Swal.fire({
        title: 'Error!',
        text: 'Please select an image to upload',
        icon: 'error',
        confirmButtonColor: '#45a049'
      });
      return;
    }

    setUploading(true);
    try {
      const base64 = await toBase64(selectedFile);
      const imageBytes = base64.split(',')[1];

      const imageData = {
        imageData: imageBytes,
        type: selectedType,
        isDeleted: false
      };

      if (editMode && currentImage) {
        const response = await authFetch(`${API_BASE_URL}/api/Images/${currentImage.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: currentImage.id,
            imageData: imageBytes,
            type: selectedType,
            isDeleted: false
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to update image');
        }
        Swal.fire({
          title: 'Success!',
          text: 'Image updated successfully',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        const response = await authFetch(`${API_BASE_URL}/api/Images`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(imageData),
        });

        if (!response.ok) {
          throw new Error('Failed to upload image');
        }
        Swal.fire({
          title: 'Success!',
          text: 'Image uploaded successfully',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false
        });
      }

      setSelectedFile(null);
      setPreviewUrl('');
      setEditMode(false);
      setCurrentImage(null);

      const response = await fetch(`${API_BASE_URL}/api/Images?type=${selectedType}`);
      if (response.ok) {
        const data = await response.json();
        setImages(data);
      }

      setError(null);
    } catch (err) {
      Swal.fire({
        title: 'Error!',
        text: 'Failed to upload image. Please try again.',
        icon: 'error',
        confirmButtonColor: '#45a049'
      });
    } finally {
      setUploading(false);
    }
  };

  const toBase64 = file => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });

  const handleDeleteImage = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'You won\'t be able to revert this!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#45a049',
      cancelButtonColor: '#7a7a7a',
      confirmButtonText: 'Yes'
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      const response = await authFetch(`${API_BASE_URL}/api/Images/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete image');
      }

      setImages(images.filter(image => image.id !== id));
      setError(null);
      Swal.fire({
        title: 'Deleted!',
        text: 'The image has been deleted.',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (err) {
      Swal.fire({
        title: 'Error!',
        text: 'Failed to delete image. Please try again.',
        icon: 'error',
        confirmButtonColor: '#45a049'
      });
    }
  };

  const handleEditImage = (image) => {
    setEditMode(true);
    setCurrentImage(image);
    setPreviewUrl(`data:image/jpeg;base64,${image.imageData}`);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setCurrentImage(null);
    setSelectedFile(null);
    setPreviewUrl('');
  };

  const handleBackToDashboard = () => {
    navigate('/admin/dashboard');
  };

  return (
    <div className="admin-image-manager-container">
      {/* Back to Dashboard Section */}
      <div className="admin-image-manager-header">
        <button
          className="admin-image-manager-back-btn"
          onClick={handleBackToDashboard}
        >
          <i className="fas fa-arrow-left"></i> Back to Dashboard
        </button>
      </div>

      <div className="admin-image-manager-title">
        <h1>{editMode ? 'Edit Image' : 'Manage Homepage Images'}</h1>
        <div className="admin-image-manager-tabs">
          <button
            className={selectedType === 'advertisement' ? 'active' : ''}
            onClick={() => {
              setSelectedType('advertisement');
              handleCancelEdit();
            }}
          >
            Advertisement Images
          </button>
          <button
            className={selectedType === 'partner' ? 'active' : ''}
            onClick={() => {
              setSelectedType('partner');
              handleCancelEdit();
            }}
          >
            Partner Images
          </button>
        </div>
      </div>

      {/* Upload New Image Section */}
      <div className="admin-image-manager-content">
        <div className="admin-image-manager-upload-section">
          <h2>{editMode ? `Edit ${selectedType} image` : `Upload new ${selectedType} image`}</h2>
          <form onSubmit={handleImageUpload}>
            <div className="admin-image-manager-file-input">
              <label htmlFor="imageUpload">
                {previewUrl ? (
                  <div className="admin-image-manager-preview">
                    <img src={previewUrl} alt="Preview" />
                  </div>
                ) : (
                  <div className="admin-image-manager-upload-placeholder">
                    <i className="fas fa-cloud-upload-alt"></i>
                    <span>Click to select an image</span>
                  </div>
                )}
                <input
                  type="file"
                  id="imageUpload"
                  accept="image/*"
                  onChange={handleFileChange}
                  hidden
                />
              </label>
            </div>
            <div className="admin-image-manager-actions">
              <button
                type="submit"
                className="admin-image-manager-upload-btn"
                disabled={uploading || !selectedFile}
              >
                {uploading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Processing...
                  </>
                ) : editMode ? (
                  <>
                    <i className="fas fa-save"></i> Save Image
                  </>
                ) : (
                  <>
                    <i className="fas fa-upload"></i> Upload Image
                  </>
                )}
              </button>
              {editMode && (
                <button
                  type="button"
                  className="admin-image-manager-cancel-btn"
                  onClick={handleCancelEdit}
                >
                  <i className="fas fa-times"></i> Cancel
                </button>
              )}
            </div>
            <p className="admin-image-manager-help-text">
              <i className="fas fa-info-circle"></i>
              JPG, JPEG, PNG only • Max 5MB per image
            </p>
          </form>
        </div>

        {/* Image Preview Section */}
        <div className="admin-image-manager-gallery">
          <h2>{selectedType === 'advertisement' ? 'Advertisement Images' : 'Partner Images'}</h2>
          {loading ? (
            <div className="admin-image-manager-loading">
              <i className="fas fa-spinner fa-spin"></i>
              <span>Loading images...</span>
            </div>
          ) : images.length === 0 ? (
            <div className="admin-image-manager-no-images">
              <i className="fas fa-images"></i>
              <span>No {selectedType} images found. Upload some images to get started.</span>
            </div>
          ) : (
            <div className="admin-image-manager-grid">
              {images.map(image => (
                <div className="admin-image-manager-item" key={image.id}>
                  <div className="admin-image-manager-image-card">
                    <img
                      src={`data:image/jpeg;base64,${image.imageData}`}
                      alt={`${selectedType} ${image.id}`}
                    />
                    <div className="admin-image-manager-image-actions">
                      <button
                        className="admin-image-manager-edit"
                        onClick={() => handleEditImage(image)}
                        title="Edit image"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="admin-image-manager-delete"
                        onClick={() => handleDeleteImage(image.id)}
                        title="Delete image"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminImageManager;